/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
    auth: {
        clientId: import.meta.env.VITE_AUTH_CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
        authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AUTH_TENANT_ID}`, // Choose SUSI as your default authority.
        // knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        // redirectUri: '/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        // postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
                if (containsPii) {
                    return;
                }

                // Filter out specific expected errors that don't indicate real problems
                const suppressedErrors = [
                    "interaction_in_progress",
                    "user session which does not exist",
                    "AADSTS160021",
                    "InteractionRequiredAuthError",
                    "BrowserAuthError",
                    "cancelled",
                    // Blocked / gesture-less popups in the widget & copilot embeds. The
                    // MSAL logger text doesn't contain "BrowserAuthError", so without
                    // these the console integration ships one Sentry error per embed
                    // load. `reportSignInFailure` already reports them with proper tags.
                    "empty_window_error",
                    "popup_window_error",
                ];

                if (suppressedErrors.some((error) => message.includes(error))) {
                    return; // Suppress these expected errors
                }

                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        // Ignore Info level messages to reduce console noise
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
            logLevel: LogLevel.Warning, // Set minimum log level to Warning
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */

export const scopesNeeded = import.meta.env.VITE_AUTH_CLIENT_ID
    ? [`${import.meta.env.VITE_AUTH_CLIENT_ID}/.default`]
    : ["email", "profile", "openid", "User.Read"];

export const loginRequest = () => ({
    scopes: scopesNeeded, // Use proper scopes for token caching
    // redirectStartPage: document.location.href,
});
